import { Box, Grid, InputAdornment, TextField } from "@mui/material";
import React from "react";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppReportTaskTypeTitlePage } from "ui/Scripts/source/models/appReportTemplate/appReportTaskTypeTitlePage";
import { ITaskTypeFieldPropUpdate } from "../../FormDesignerTypes";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { AddToolButton } from "source/components/framework/Button/AddToolButton";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { FileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { AppUtils } from "source/framework/appUtils";
import { Base } from "ui/Scripts/source/framework/base";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown/IdTitleDropdown";
import { AppReportTaskTypeTitlePageLayout } from "ui/Scripts/source/models/enums";

interface IFormDesignerComponentPropertiesTitlePageProps {
    formFieldMargin: string;
    taskTypeCol?: ITaskTypeCol;
    onTaskTypePropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
}

export const FormDesignerComponentPropertiesTitlePage = (
    props: IFormDesignerComponentPropertiesTitlePageProps
) => {
    const handleObjectValueChange = (obj) => {
        props.onTaskTypePropUpdate([{
            field: props.taskTypeCol?.field,
            name: "objectValue",
            value: obj
        }]);
    };

    const objectValue = new AppReportTaskTypeTitlePage(props.taskTypeCol.objectValue);

    const handleNamedChange = (event) => {
        objectValue.name = event.target.value;
        handleObjectValueChange(objectValue);
    };

    const handleDescriptionChange = (event) => {
        objectValue.description = event.target.value;
        handleObjectValueChange(objectValue);
    };

    const handleImageChange = async (file: File) => {
        if (!file) return;
        const attachment = await FileAttachment.fileToFileAttachment(file, 0);
        if (!AppUtils.validateFileAttachment(attachment)) return;
        objectValue.imageName = attachment.name;
        objectValue.image = await Base.blobToBase64(attachment.file);
        handleObjectValueChange(objectValue);
    };

    const handleImageRemove = async () => {
        objectValue.imageName = "";
        objectValue.image = "";
        handleObjectValueChange(objectValue);
    };

    return (
        <>
            <Grid item sm={12}>
                <FormField title={Translations.Layout} margin={props.formFieldMargin} required={true}>
                    <IdTitleDropdown
                        selectedId={objectValue.layout.toString()}
                        items={[
                            { id: AppReportTaskTypeTitlePageLayout.Layout1.toString(), title: Translations.FormTitlePageLayout1 }
                        ]}
                        onChange={(id: string) => {
                            objectValue.layout = parseInt(id) as AppReportTaskTypeTitlePageLayout;
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.Title} margin={props.formFieldMargin} required={true}>
                    <TextField
                        value={objectValue.name}
                        minRows={3}
                        multiline={true}
                        onChange={handleNamedChange}
                        onBlur={(e) => {
                            if (e.target.value) return;
                            objectValue.name = props.taskTypeCol?.field ?? Translations.Title;
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.Description} margin={props.formFieldMargin}>
                    <TextField
                        value={objectValue.description}
                        minRows={5}
                        multiline={true}
                        onChange={handleDescriptionChange}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.Image} margin={props.formFieldMargin}>
                    <TextField
                        value={objectValue.imageName ?? ""}
                        sx={
                            {
                                ".MuiInputBase-root": {
                                    paddingRight: 0
                                }
                            }
                        }
                        InputProps={{
                            inputProps: { readOnly: true, disabled: true },
                            endAdornment:
                                <InputAdornment position="end">
                                    <AddToolButton
                                        tooltip={Translations.AddImage}
                                        onClick={null}
                                        onFileClick={handleImageChange}
                                    />
                                    <RemoveToolButton
                                        disabled={!objectValue.image}
                                        tooltip={Translations.RemoveImage}
                                        onClick={handleImageRemove}
                                    />
                                </InputAdornment>
                        }}
                    />
                    {!!objectValue.image &&
                        <Box
                            component="img"
                            sx={{
                                marginTop: "0.5rem",
                                maxHeight: "400px",
                                maxWidth: "100%",
                                border: "1px solid #ccc",
                                borderRadius: 1,
                                objectFit: "contain",
                            }}
                            src={objectValue.image}
                        >
                        </Box>
                    }
                </FormField>
            </Grid>
        </>
    );
};
